(ns ductile.ui.http
  "Tiny http helper for making transit-encoded requests."
  (:require [ductile.ui.transit :as transit]
            [nextjournal.clerk.render.hooks :as hooks]
            [nextjournal.clerk.render.localstorage :as localstorage]
            [re-frame.core :as rf]
            [reagent.core :as reagent]
            [reitit.impl]))


(def transit-headers
  #js {"Accept" "application/transit+json"
       "Content-Type" "application/transit+json"})

(defn read-transit-body [url-or-handler-name body]
  (try (transit/read body)
       (catch js/Error e
         (js/console.error e)
         (throw (ex-info (ex-message e)
                         {:error ::transit-read-error
                          :error-message (ex-message e)
                          :url url-or-handler-name
                          :response-body body})))))

(defn read-transit-response [request url-or-handler-name]
  (-> request
      (.then (fn [response] (.text response)))
      (.then (partial read-transit-body url-or-handler-name))))

(defn $get
  "Performs a `get` request to the given `url`, returns a promise."
  [url]
  (read-transit-response (js/fetch url #js {:headers transit-headers})
                         url))

(defn $post
  "Performs a `post` request to the given `url` with transit-encoded `body-params`, returns a promise."
  [url body-params]
  (read-transit-response (js/fetch url
                                   #js {:method "post"
                                        :headers transit-headers
                                        :body (transit/write body-params)})
                         (:lambda/handler body-params)))

(defn download
  [url body-params]
  (-> (js/fetch url #js {:method "post"
                         :headers transit-headers
                         :body (transit/write body-params)})
      (.then (fn [res]
               (if (= 200 (.-status res))
                 (let [[_ file-name] (some->> (.get (.-headers res) "Content-Disposition")
                                              (re-matches #".*filename=\"(.*)\".*"))
                       file-name (or file-name "Download")
                       blob (.blob res)]
                   (.then blob (fn [blob]
                                 (if (= 0 (.-size blob))
                                   {:error :data-export-failed
                                    :error-message "Unable to export data, try exporting less data"}
                                   (let [url (.createObjectURL js/window.URL blob)
                                         a (.createElement js/document "a")]
                                     (set! (.-style a) "display:none")
                                     (set! (.-download a) file-name)
                                     (set! (.-href a) url)
                                     (.click a)
                                     (.revokeObjectURL js/window.URL blob)
                                     blob)))))
                 ;; non-200: parse and return the error data
                 (.then (.text res) (partial read-transit-body (.-url res))))))))

(defn download-lambda
  [params]
  (download "/api/lambda" params))

(def speculative-transactions-storage-key
  "ductile.speculative-transactions")

(def !speculative-transaction-opts
  (reagent/atom (localstorage/get-item speculative-transactions-storage-key)))

(defn speculative-transactions? []
  (some? @!speculative-transaction-opts))

(defn set-speculative-transaction-opts! [opts]
  (reset! !speculative-transaction-opts opts)
  (if opts
    (when (not= :single-browser-tab
                (:ductile.datomic.speculative-transactions/scope opts))
      (localstorage/set-item! speculative-transactions-storage-key opts))
    (localstorage/remove-item! speculative-transactions-storage-key)))

(defn read-front-end-hash! []
  ;; hack using re-frame to avoid circular dep
  @(rf/subscribe [:ductile.ui.deframe/get :ductile.ui.hashing/front-end-hash]))

(defn $lambda
  "Performs a `post` request to the given lambda handler identified by
  the fully-qualified `handler-sym` with transit-enoded `body-params`,
  returns a promise."
  [handler-sym params]
  (let [handler-ns (namespace handler-sym)
        handler-name (name handler-sym)]
    (-> ($post (str "/api/lambda/" (reitit.impl/url-encode handler-ns) "/" (reitit.impl/url-encode handler-name))
               (cond-> (assoc params
                              :ductile.ui.hashing/front-end-hash (read-front-end-hash!)
                              :lambda/handler handler-sym)
                 (speculative-transactions?) (merge @!speculative-transaction-opts)))
        (.then (fn [lambda-response]
                 (if-let [enabled-speculative-transactions-opts (and (map? lambda-response)
                                                                     (:ductile.lambda/enabled-speculative-transactions-opts lambda-response))]
                   (do (set-speculative-transaction-opts! enabled-speculative-transactions-opts)
                       (dissoc lambda-response :ductile.lambda/enabled-speculative-transactions-opts))
                   lambda-response))))))

(defn ^:export $enable-speculative-transactions!
  ([] ($enable-speculative-transactions! {}))
  ([enable-params]
   (if-let [opts @!speculative-transaction-opts]
     (js/Promise.resolve {:error :already-enabled
                          :error-message "Speculative transactions are already enabled, cannot enable again"
                          :speculative-transaction-opts opts})
     (-> ($lambda 'ductile.datomic.speculative-transactions/enable! enable-params)
         (.then (fn [enable-response]
                  (if (:error enable-response)
                    (do
                      (rf/dispatch ['ductile.ui.deframe/tx [[:op/assoc-in [:current-route/transient ::speculative-transaction-error] enable-response]]])
                      (js/console.error :$enable-speculative-transactions! enable-response))
                    (do
                      (rf/dispatch ['ductile.ui.deframe/tx [[:op/dissoc-in [:current-route/transient ::speculative-transaction-error]]]])
                      (js/console.log '$enable-speculative-transactions! enable-response)
                      (set-speculative-transaction-opts! (-> enable-response
                                                             (select-keys [:ductile.datomic.speculative-transactions/uid
                                                                           :ductile.datomic.speculative-transactions/scope])
                                                             (assoc :ductile.ui.router/return-to (.-pathname js/window.location))))))
                  enable-response))))))

#_($enable-speculative-transactions!)

(defn $disable-speculative-transactions! []
  (if-let [opts @!speculative-transaction-opts]
    (-> ($lambda 'ductile.datomic.speculative-transactions/disable! opts)
        (.then (fn [disable-response]
                 (js/console.log '$disable-speculative-transactions! disable-response)
                 (set-speculative-transaction-opts! nil)
                 (when-let [return-to (:ductile.ui.router/return-to opts)]
                   (rf/dispatch ['ductile.ui.deframe/perform-effects {'ductile.ui.router/navigate-to return-to}])))))
    (js/console.warn '$disable-speculative-transactions! :not-enabled)))

#_($disable-speculative-transactions!)

(defn use-lambda
  "React hook to perform a lambda request to load inital data. Will be memoized."
  [handler-sym params]
  (hooks/use-promise (hooks/use-memo #($lambda handler-sym params))))

(defn $vehicle-search [opts]
  ;; TODO: drop `seq`, currently needed because TransformedCountedSeq can't yet be transit encoded
  ;; see https://clojurians.slack.com/archives/C03S1KBA2/p1700409641820449
  ($lambda 'ductile.vehicle-search/use-case-search (merge {:limit 1000} opts)))

(defn $logout []
  ($post "/logout" {}))

(defn $edi-vin-search [vin ductile-id]
  ($lambda 'ductile.edi.db/search {:vehicle/vin vin :ductile/id ductile-id}))

(defn active-transport-orders [compound-name]
  ($lambda 'ductile.datomic.query/active-transport-orders {:compound-name compound-name}))

(defn announcements [compound-name]
  ($lambda 'ductile.datomic.query/announcements {:compound-name compound-name}))

(defn stored-invoice [params]
  ($lambda 'ductile.billing/$load! params))

(defn invoices [params]
  ($lambda 'ductile.invoices/$list params))

(defn fahrzeug-storage-billings [params]
  ($lambda 'ductile.billing/$fahrzeug-storage-billings params))

(defn ^:export $lambda-json
  "Helper for use in ui-tests."
  [handler-name args]
  (-> ($lambda (symbol handler-name) (js->clj args {:keywordize-keys true}))
      (.then clj->js)))

(defn ^:export $vehicle-search-json
  "Helper for use in ui-tests."
  [opts]
  (-> ($vehicle-search (merge {:limit 10 :ductile.vehicle-search/use-case :ui-test-json}
                              (js->clj opts {:keywordize-keys true})))
      (.then clj->js)))
